import * as React from "react";
import { useRef, useState, useEffect } from "react";
import { classNames } from "../lib/Util";

import "./SelectableSvg.scss";

/**
 * "around"
 * The selection rectangle must completely enclose the target element
 * (all sides of the selection rectangle are outside the element's bounds).
 *
 * "partial"
 * The selection rectangle can overlap with any part of the target element
 * or be inside of it without an intersection.
 */
export type SelectionMode = "around" | "partial";

export type SelectionArgs = {
    selected_els: SVGElement[];
    area_rect: DOMRect;
} | null;

export type Selection = {
    selected_els: SVGElement[];
    area_rect: DOMRect;
    first_selected_el: Element;
} | null;

type Props = {
    children: React.ReactNode;
    className?: string;
    target_selector?: string;
    selection_mode?: SelectionMode;
    onSelect?: (selection: Selection) => void;
};

const RECT_SELECTION_CLASS = "rect-selection";
const DEFAULT_SELECTION_MODE: SelectionMode = "around";

const SelectionStrategy: Record<SelectionMode, (selection_rect: DOMRect, el_rect: DOMRect) => boolean> = {
    around: (selection_rect: DOMRect, el_rect: DOMRect): boolean => {
        return (
            selection_rect.left <= el_rect.left &&
            selection_rect.right >= el_rect.right &&
            selection_rect.top <= el_rect.top &&
            selection_rect.bottom >= el_rect.bottom
        );
    },
    partial: (selection_rect: DOMRect, el_rect: DOMRect): boolean => {
        return (
            selection_rect.left < el_rect.right &&
            selection_rect.right > el_rect.left &&
            selection_rect.top < el_rect.bottom &&
            selection_rect.bottom > el_rect.top
        );
    },
};

/**
 * SelectableSvg provides an SVG wrapper that allows selecting elements by drawing a rectangle.
 *
 * Usage:
 * Wrap your SVG content in <SelectableSvg> and provide a target_selector if you want only
 * specific SVG elements to be selectable. Use onSelect/onSelect to react to selection events.
 */
const SelectableSvg = (props: Props) => {
    const rectSelectionRef = useRef<SVGRectElement | null>(null);
    const selectableAreaRef = useRef<SVGRectElement | null>(null);

    const selectable_area_rect = selectableAreaRef.current?.getBoundingClientRect();

    const [target_els, setTargetEls] = useState<SVGElement[]>([]);
    const firstSelectedElRef = useRef<SVGElement | null>(null);

    const [mousedown, setMouseDown] = useState(false);
    const [lastMouseX, setLastMouseX] = useState(0);
    const [lastMouseY, setLastMouseY] = useState(0);
    const [selectionX, setSelectionX] = useState(0);
    const [selectionY, setSelectionY] = useState(0);

    const checkSelection = SelectionStrategy[props.selection_mode ?? DEFAULT_SELECTION_MODE];

    useEffect(() => {
        if (mousedown && selectableAreaRef.current) {
            const els = selectTargetEls(props.target_selector) as SVGElement[];
            if (els.length > 0) {
                setTargetEls(els);
            }
        }
    }, [mousedown]);

    const handleMouseDown = (event: React.MouseEvent) => {
        event.preventDefault();
        firstSelectedElRef.current = null;
        setLastMouseX(selectionX);
        setLastMouseY(selectionY);
        setMouseDown(true);
        window.dispatchEvent(new Event("tooltip-disable"));
    };

    const handleMouseMove = (event: React.MouseEvent<SVGSVGElement>) => {
        if (!selectable_area_rect) {
            return;
        }

        if (mousedown && !firstSelectedElRef.current) {
            const selection = selectSelectedEls();
            if (selection) {
                firstSelectedElRef.current = selection.selected_els[0];
            }
        }

        setSelectionX(event.clientX - selectable_area_rect.left);
        setSelectionY(event.clientY - selectable_area_rect.top);
    };

    const handleMouseUp = () => {
        if (!mousedown || !rectSelectionRef.current) {
            return;
        }

        setMouseDown(false);
        window.dispatchEvent(new Event("tooltip-enable"));

        if (!props.onSelect) {
            return;
        }

        const selection = selectSelectedEls();
        if (!selection) {
            props.onSelect(null);
            return;
        }

        const first_selected_el = firstSelectedElRef.current ?? selection.selected_els[0];
        const { selected_els, area_rect } = selection;
        props.onSelect({ selected_els, area_rect, first_selected_el });
        firstSelectedElRef.current = null;
        setTargetEls([]);
    };

    const selectTargetEls = (selector: string = `:not(.${RECT_SELECTION_CLASS})`) => {
        if (!selectableAreaRef.current) {
            return [];
        }

        return Array.from(selectableAreaRef.current.parentNode?.querySelectorAll(selector) || []);
    };

    const selectSelectedEls = (): SelectionArgs => {
        const area_rect = rectSelectionRef.current?.getBoundingClientRect();
        if (!area_rect) {
            return null;
        }

        // Just a click without dragging essentially -> clear the selection.
        if (area_rect.width === 0 && area_rect.height === 0) {
            return null;
        }

        const selected_els: SVGElement[] = target_els.filter(el => {
            const el_rect = el.getBoundingClientRect();
            return checkSelection(area_rect, el_rect);
        });

        if (selected_els.length === 0) {
            return null;
        }

        return {
            selected_els,
            area_rect,
        };
    };

    const renderRectangle = (): React.ReactNode => {
        const width = Math.abs(selectionX - lastMouseX);
        const height = Math.abs(selectionY - lastMouseY);

        // Update top-left corner of the rectangle when
        // mouse is moved to the left or up.
        const x = selectionX < lastMouseX ? selectionX : lastMouseX;
        const y = selectionY < lastMouseY ? selectionY : lastMouseY;

        return (
            <rect
                ref={rectSelectionRef}
                className={RECT_SELECTION_CLASS}
                x={x}
                y={y}
                height={height}
                width={width}
                style={{ visibility: mousedown ? "visible" : "hidden" }}
            />
        );
    };

    return (
        <g
            className={classNames("selectable-svg", props.className, {
                "selectable-svg--selecting": mousedown,
            })}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
        >
            <rect
                ref={selectableAreaRef}
                height="100%"
                width="100%"
                fill="transparent"
                className="selectable-area"
            ></rect>
            {props.children}
            {renderRectangle()}
        </g>
    );
};

export default SelectableSvg;
